import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Spin,
  Space,
  Upload,
  InputNumber,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  VideoCameraOutlined,
  EyeOutlined,
  SortAscendingOutlined,
  PictureOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import masterService from "../../../services/masterService";
import { getImageUrl } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;
const CATEGORY_IMAGE_ASPECT = 320 / 180;

const EditMasterCategory = () => {
  const { category_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [, setImageUrl] = useState("");
  const [, setLogoUrl] = useState("");
  const [languages, setLanguages] = useState([]);
  const [multilingualData, setMultilingualData] = useState([]);
  const [imageSets, setImageSets] = useState([]);
  const [categoryDetailsData, setCategoryDetailsData] = useState(null);
  const [hasInitializedImageSets, setHasInitializedImageSets] = useState(false);
  const notify = useNotify();

  const buildImageSetsFromCategory = (categoryData) => {
    if (Array.isArray(categoryData?.images_arr) && categoryData.images_arr.length > 0) {
      return categoryData.images_arr.map((img) => ({
        lang_id: img?.lang_id || null,
        card_image: img?.card_image || "",
        movie_logo: img?.movie_logo || "",
      }));
    }
    return [];
  };

  const fetchCategoryDetails = useCallback(async () => {
    if (!category_id) {
      notify.error({ message: "Category ID is required" });
      navigate("/categories");
      return;
    }

    setLoadingData(true);
    try {
      const response = await masterService.getSingleMasterCategory(category_id);

      if (response.data && response.data.status) {
        // API now returns a single object, not an array
        const genreData = response.data.data;
        console.log("EditMasterCategory: API response data", genreData);
        console.log("EditMasterCategory: category_id from params", category_id);

        // Check for both vc_name and category_name to handle different API response formats
        const categoryName = genreData?.vc_name || genreData?.category_name;
        if (genreData && categoryName) {
          form.setFieldsValue({
            category_name: categoryName,
            category_is_active: genreData.vc_is_active || genreData.category_is_active || "Y",
            show_in_preference: genreData.show_in_preference ? String(genreData.show_in_preference).trim() : "N",
            preference_order_number: parseInt(genreData.order || genreData.preference_order_number || 0),
            image_url: genreData.card_image || genreData.image_url || "",
            logo_url: genreData.banner_image || genreData.logo_url || "",
            category_description: genreData.vc_description || genreData.category_description || "",
            category_keywords: genreData.vc_keywords || genreData.category_keywords
              ? String(genreData.vc_keywords || genreData.category_keywords)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
              : [],
          });
          // Set state variables for image previews
          setImageUrl(genreData.card_image || genreData.image_url || "");
          setLogoUrl(genreData.banner_image || genreData.logo_url || "");
          setCategoryDetailsData(genreData);

          // Prefill multilingual data if available
          if (Array.isArray(genreData.multilingual_data)) {
            setMultilingualData(
              genreData.multilingual_data.map((d) => ({
                lang_id: d.lang_id ?? null,
                category_name: d.vc_name ?? d.category_name ?? "",
                category_description: d.vc_description ?? d.category_description ?? "",
                category_keywords: d.vc_keywords || d.category_keywords
                  ? String(d.vc_keywords || d.category_keywords)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                  : [],
              }))
            );
          }
        } else {
          console.error("EditMasterCategory: Category data missing required fields", genreData);
          notify.error({ message: "Category data not found or missing required fields" });
          navigate("/categories");
        }
      } else {
        console.error("EditMasterCategory: API returned error", response.data);
        notify.error({
          message: response.data?.message || "Failed to fetch category details",
        });
        navigate("/categories");
      }
    } catch (error) {
      console.error("Error fetching category details:", error);
      notify.error({ message: "Error fetching category details" });
      navigate("/categories");
    } finally {
      setLoadingData(false);
    }
  }, [category_id, form, navigate, notify]);

  useEffect(() => {
    setPageTitle("Edit Category");
    fetchCategoryDetails();
  }, [fetchCategoryDetails, setPageTitle]);

  // Load languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const params = {
          perPage: 1000,
          currentPage: 1,
          searchText: "",
          status: "Y",
        };
        const res = await masterService.getVideoLanguages(params);
        const list =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setLanguages(list);
      } catch (e) {
        notify.error({ message: "Failed to load languages" });
      }
    };
    loadLanguages();
  }, [notify]);

  useEffect(() => {
    if (!categoryDetailsData || hasInitializedImageSets) {
      return;
    }
    setImageSets(buildImageSetsFromCategory(categoryDetailsData));
    setHasInitializedImageSets(true);
  }, [categoryDetailsData, hasInitializedImageSets]);

  const addImageSet = () => {
    setImageSets([
      ...imageSets,
      {
        lang_id: null,
        card_image: "",
        movie_logo: "",
      },
    ]);
  };

  const handleImageSetRemove = (index) => {
    setImageSets(imageSets.filter((_, i) => i !== index));
  };

  const onImageUploadSets = async (file, keys) => {
    if (!file?.type?.startsWith("image/")) {
      notify.error({ message: "You can only upload image files" });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "category");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      const uploadedUrl = response.data.data.s3_key;
      const updatedImageSets = [...imageSets];
      updatedImageSets[keys.index][keys.path] = uploadedUrl;
      setImageSets(updatedImageSets);

      notify.success({ message: "Image uploaded successfully" });
    } catch (e) {
      notify.error({ message: "Failed to upload image" });
    }
  };

  // Multilingual helpers
  const addMultilingualData = () => {
    setMultilingualData([
      ...multilingualData,
      {
        lang_id: null,
        category_name: "",
        category_description: "",
        category_keywords: [],
      },
    ]);
  };
  const handleMultilingualDataRemove = (index) => {
    setMultilingualData(multilingualData.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values) => {
    // Validate multilingual data: no duplicate languages and require name if language is selected
    const langs = multilingualData.map((d) => d.lang_id).filter(Boolean);
    if (new Set(langs).size !== langs.length) {
      notify.error({ message: "Duplicate languages in multilingual data" });
      return;
    }
    for (let i = 0; i < multilingualData.length; i++) {
      const d = multilingualData[i];
      if (d.lang_id && !d.category_name) {
        notify.error({
          message: `Language entry #${i + 1
            }: Category name is required when language is selected`,
        });
        return;
      }
    }

    // Validate image sets
    if (imageSets.length > 0) {
      const invalidImageSets = imageSets.some(
        (img) => !img || !img.lang_id || !img.card_image
      );
      if (invalidImageSets) {
        notify.error({
          message:
            "Please complete all image sets (language and card image are required)",
        });
        return;
      }
      const imageLangs = imageSets.map((img) => img.lang_id).filter(Boolean);
      if (new Set(imageLangs).size !== imageLangs.length) {
        notify.error({ message: "Duplicate languages in image sets" });
        return;
      }
    }

    setLoading(true);
    try {
      const parentKeywords = Array.isArray(values.category_keywords)
        ? values.category_keywords
          .map((s) => String(s).trim())
          .filter(Boolean)
          .join(",")
        : values.category_keywords || "";
      const multilingualSerialized = (multilingualData || []).map((d) => ({
        ...d,
        category_keywords: Array.isArray(d.category_keywords)
          ? d.category_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
          : d.category_keywords || "",
      }));

      const normalizedImageSets = (imageSets || []).map((img) => ({
        lang_id: img?.lang_id || null,
        card_image: img?.card_image || "",
        movie_logo: img?.movie_logo || "",
      }));

      const payload = {
        ...values,
        category_id: category_id,
        category_description: values.category_description || "",
        category_keywords: parentKeywords,
        show_in_preference: values.show_in_preference || "N",
        preference_order_number: values.preference_order_number || 0,
        multilingual_data: multilingualSerialized,
        images_arr: normalizedImageSets,
      };

      const response = await masterService.updateMasterCategory(category_id, payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Category updated successfully",
        });
        navigate("/categories");
      } else {
        notify.error({
          message: response.data.message || "Failed to update category",
        });
      }
    } catch (error) {
      console.error("Error updating category:", error);
      notify.error({ message: "Error updating category" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setImageSets([]);
    setHasInitializedImageSets(false);
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/categories");
  };

  return (
    <div className="min-h-screen">
      <div className="">
        {/* Header Section */}

        <Spin spinning={loadingData}>
          <Card>
            <Form
              form={form}
              layout="vertical"
              size="large"
              onFinish={handleSubmit}
              className="space-y-6"
            >
              {/* Basic Information Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <VideoCameraOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Basic Information
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Categories Name
                        </span>
                      }
                      name="category_name"
                      rules={[
                        { required: true, message: "Please enter category name" },
                      ]}
                    >
                      <Input
                        placeholder="Enter category name"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Status
                        </span>
                      }
                      name="category_is_active"
                    >
                      <Select
                        placeholder="Select status"
                        className="rounded-lg"
                      >
                        <Option value="Y">
                          <Badge status="success" text="Active" />
                        </Option>
                        <Option value="N">
                          <Badge status="default" text="Inactive" />
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Description
                        </span>
                      }
                      name="category_description"
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Enter description"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Keywords
                        </span>
                      }
                      name="category_keywords"
                    >
                      <Select
                        mode="tags"
                        placeholder="Type a keyword and press Enter"
                        tokenSeparators={[","]}
                        open={false}
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Preference Settings Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <EyeOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Preference Settings
                  </Title>
                </div>

                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Show in Preference
                        </span>
                      }
                      name="show_in_preference"
                    >
                      <Select
                        placeholder="Select preference visibility"
                        className="rounded-lg"
                      >
                        <Option value="Y">
                          <Badge status="success" text="Yes" />
                        </Option>
                        <Option value="N">
                          <Badge status="default" text="No" />
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label={
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Preference Order
                        </span>
                      }
                      name="preference_order_number"
                      rules={[
                        {
                          pattern: /^(0|[1-9]\d*)$/,
                          message:
                            "Preference order number must be a non-negative integer",
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="Enter preference order number"
                        style={{ width: "100%" }}
                        className="rounded-lg"
                        addonBefore={<SortAscendingOutlined />}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
              {/* Media Assets Section */}

              {/* Image Sets Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <PictureOutlined />
                    <Title level={4} className="!mb-0" style={{ color: "rgba(0, 0, 0, 0.88)" }}>
                      Image Sets
                    </Title>
                  </div>
                  <Button
                    type="dashed"
                    onClick={addImageSet}
                    icon={<PlusOutlined />}
                  >
                    Add Image Set
                  </Button>
                </div>

                {imageSets.length === 0 && (
                  <Card style={{ textAlign: "center", background: "#fafafa" }}>
                    <Text type="secondary">No image sets added yet.</Text>
                  </Card>
                )}

                <div className="space-y-4">
                  {imageSets.map((image, index) => (
                    <Card
                      key={`category-image-${index}`}
                      title={
                        <div className="flex items-center justify-between">
                          <Text strong>
                            Image Set #{index + 1}
                            {image.lang_id &&
                              ` - ${languages.find((l) => l.lang_id === image.lang_id)
                                ?.lang_name || "Unknown Language"
                              }`}
                          </Text>
                          <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleImageSetRemove(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      }
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24}>
                          <Form.Item label="Language" required style={{ marginBottom: 0 }}>
                            <Select
                              value={image.lang_id}
                              placeholder="Select language"
                              onChange={(value) => {
                                const arr = [...imageSets];
                                arr[index].lang_id = value;
                                setImageSets(arr);
                              }}
                              showSearch
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                            >
                              {languages.map((lang) => (
                                <Option key={lang.lang_id} value={lang.lang_id}>
                                  {lang.lang_name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label="Category Card Image">
                            {image.card_image && (
                              <div className="mb-4">
                                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                                  <Text strong className="text-orange-700 text-sm">
                                    {getImageUrl(image.card_image)}
                                  </Text>
                                </div>
                                <div className="w-full h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                                  <img
                                    src={getImageUrl(image.card_image)}
                                    alt="Card preview"
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                              </div>
                            )}
                            <ImgCrop
                              aspect={CATEGORY_IMAGE_ASPECT}
                              rotationSlider
                              quality={1}
                            >
                              <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={(f) => {
                                  onImageUploadSets(f, { index, path: "card_image" });
                                  return false;
                                }}
                              >
                                <Button
                                  icon={<UploadOutlined />}
                                  className="w-full rounded-lg"
                                  type={image.card_image ? "default" : "primary"}
                                >
                                  {image.card_image ? "Change Card Image" : "Upload Card Image"}
                                </Button>
                              </Upload>
                            </ImgCrop>
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                          <Form.Item label="Category Logo">
                            {image.movie_logo && (
                              <div className="mb-4">
                                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                                  <Text strong className="text-orange-700 text-sm">
                                    {getImageUrl(image.movie_logo)}
                                  </Text>
                                </div>
                                <div className="w-full h-32 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                                  <img
                                    src={getImageUrl(image.movie_logo)}
                                    alt="Logo preview"
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                              </div>
                            )}
                            <Upload
                              accept="image/*"
                              showUploadList={false}
                              beforeUpload={(f) => {
                                onImageUploadSets(f, { index, path: "movie_logo" });
                                return false;
                              }}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                className="w-full rounded-lg"
                                type={image.movie_logo ? "default" : "primary"}
                              >
                                {image.movie_logo ? "Change Logo" : "Upload Logo"}
                              </Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Multilingual Data Section */}
              <div
                className="bg-white p-6 rounded-lg border border-gray-200"
                style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
              >
                <div className="flex items-center justify-between mb-4">
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Multilingual Data
                  </Title>
                  <Button
                    type="dashed"
                    onClick={addMultilingualData}
                    icon={<UploadOutlined />}
                  >
                    Add Language
                  </Button>
                </div>

                {multilingualData.length === 0 && (
                  <Card style={{ textAlign: "center", background: "#fafafa" }}>
                    <Text type="secondary">
                      No multilingual data added yet. Click "Add Language" to
                      get started.
                    </Text>
                  </Card>
                )}

                <div className="space-y-4">
                  {multilingualData.map((data, index) => (
                    <Card
                      key={`ml-${index}`}
                      title={
                        <div className="flex items-center justify-between">
                          <Text strong>
                            Language Entry #{index + 1}
                            {data.lang_id &&
                              ` - ${languages.find(
                                (l) => l.lang_id === data.lang_id
                              )?.lang_name || "Unknown Language"
                              }`}
                          </Text>
                          <Button
                            type="link"
                            danger
                            onClick={() => handleMultilingualDataRemove(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      }
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Language"
                            required
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              value={data.lang_id}
                              placeholder="Select language"
                              onChange={(value) => {
                                const arr = [...multilingualData];
                                arr[index].lang_id = value;
                                setMultilingualData(arr);
                              }}
                              showSearch
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                            >
                              {languages.map((lang) => (
                                <Option key={lang.lang_id} value={lang.lang_id}>
                                  {lang.lang_name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Category Name"
                            required
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              value={data.category_name}
                              placeholder="Enter category name in this language"
                              onChange={(e) => {
                                const arr = [...multilingualData];
                                arr[index].category_name = e.target.value;
                                setMultilingualData(arr);
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Description"
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea
                              rows={3}
                              value={data.category_description}
                              placeholder="Enter description in this language"
                              onChange={(e) => {
                                const arr = [...multilingualData];
                                arr[index].category_description = e.target.value;
                                setMultilingualData(arr);
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Keywords"
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              mode="tags"
                              value={
                                Array.isArray(data.category_keywords)
                                  ? data.category_keywords
                                  : data.category_keywords
                                    ? String(data.category_keywords)
                                      .split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean)
                                    : []
                              }
                              placeholder="Type a keyword and press Enter"
                              onChange={(value) => {
                                const arr = [...multilingualData];
                                arr[index].category_keywords = value;
                                setMultilingualData(arr);
                              }}
                              tokenSeparators={[","]}
                              open={false}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div
                className="flex justify-end gap-4 pt-6 border-t"
                style={{ borderColor: "#d9d9d9" }}
              >
                <Space size="middle">
                  <Button
                    onClick={handleReset}
                    icon={<ReloadOutlined />}
                    size="large"
                    className="px-6"
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleGoBack}
                    icon={<ArrowLeftOutlined />}
                    size="large"
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SaveOutlined />}
                    size="large"
                    className="px-8"
                  >
                    {loading ? "Updating..." : "Update Category"}
                  </Button>
                </Space>
              </div>
            </Form>
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default EditMasterCategory;
